import { i18n as I18nType, TFunction, TOptionsBase } from "i18next"
import deprecatedCommonTranslations from "./translations/en/deprecatedCommon.json"
import { Translation, TranslationJSON } from "./types"

const capitalize = (str: string): string =>
  str ? `${str[0].toUpperCase()}${str.slice(1)}` : ""

const capitalizeAllWords = (str: string): string =>
  str ? str.split(" ").map(capitalize).join(" ") : str

// format a translation based on the format option provided to the
// translate function
export const addTranslationFormatters = (i18next: I18nType): void => {
  if (!i18next.services.formatter) return

  i18next.services.formatter.add(
    "lowercase",
    (value: any, lng: string | undefined, options: any): string => {
      return String(value).toLowerCase()
    }
  )

  i18next.services.formatter.add(
    "uppercase",
    (value: any, lng: string | undefined, options: any): string => {
      return String(value).toUpperCase()
    }
  )

  i18next.services.formatter.add(
    "capitalize",
    (value: any, lng: string | undefined, options: any): string => {
      return capitalize(String(value))
    }
  )
}

// Utility type to override TFunction's return type to always be string while preserving all overloads
type TFunctionReturningString = {
  // Basic usage
  <TKeys extends string | TemplateStringsArray = string>(
    key: TKeys | TKeys[]
  ): string

  // With options (covering all the main overload patterns)
  <TKeys extends string | TemplateStringsArray = string>(
    key: TKeys | TKeys[],
    options?: (TOptionsBase & Record<string, unknown>) | string
  ): string

  // Overloaded usage with defaultValue
  <TKeys extends string | TemplateStringsArray = string>(
    key: TKeys | TKeys[],
    defaultValue?: string,
    options?: (TOptionsBase & Record<string, unknown>) | string
  ): string
}

// Type for translation methods that always return string
// This is because i18n's TFunction type allows a returned object or string,
// but we know we'll always be returning a string.
export type TranslateMethods = {
  t: TFunctionReturningString
  tc: TFunctionReturningString
  tca: TFunctionReturningString
  conditionalT: (
    key: string | string[],
    options?: TOptionsBase & Record<string, unknown>,
    customTranslator?: TFunction
  ) => string
}

// These keys correspod directly to keys used in the translation json files. Update with care!
export const getTranslateMethods = (
  translator: TFunction,
  i18nInstance: I18nType
): TranslateMethods => ({
  t: ((...args: Parameters<TFunction>) =>
    translator(...args)) as TranslateMethods["t"],
  tc: ((...args: Parameters<TFunction>) =>
    capitalize(String(translator(...args)))) as TranslateMethods["tc"],
  tca: ((...args: Parameters<TFunction>) =>
    capitalizeAllWords(String(translator(...args)))) as TranslateMethods["tca"],
  conditionalT: (key, options, customTranslator) =>
    i18nInstance.exists(key)
      ? (customTranslator || translator)(key, options)
      : ""
})
// Handles processing legacy custom translator methods to proper i18next nested keys.
export const preProcessTranslation = (() => {
  // group the (translator key) and (translation key)
  const re = new RegExp(
    `(?<translationMethod>t|tc|tca)\\('(?<translationKey>[a-zA-Z0-9._-]+)'\\)`,
    "g"
  )

  const process = (...args: unknown[]): string => {
    // Get grouped match
    const { translationMethod, translationKey } = args.slice(-1)[0] as {
      translationMethod: string
      translationKey: string
    }
    switch (translationMethod) {
      case "t":
        return `$t(${translationKey})`
      case "tc":
        return `$t(${translationKey}, capitalize)`
      case "tca":
        return `$t(${translationKey}, capitalize_all)`
      default:
        return ""
    }
  }

  const fn = (translationJSON: TranslationJSON): TranslationJSON => {
    if (!translationJSON) return {}

    return Object.entries(translationJSON).reduce((json, [key, value]) => {
      // if value is a string, record the path/value
      if (typeof value === "string") {
        return {
          ...json,
          [key]: value.replace(re, (match, ...args) => process(...args))
        }
      }

      // otherwise, continue to flatten the json
      return {
        ...json,
        [key]: fn(value as TranslationJSON)
      }
    }, {} as TranslationJSON)
  }

  return fn
})()

// For local testing
export const getLocalTranslation = async (
  language: string | null,
  namespace: string
): Promise<Translation | null> => {
  if (!language) {
    return null
  }

  const translation = await import(
    `./translations/${language}/${namespace}.json`
    /* webpackChunkName: "translation-json" */
  )

  return {
    translation,
    language,
    id: -1
  }
}

// NOTE: this function mutates the translation object!
// Apply deprecated translations to the provided translation. This maps
// deprecated translation keys to new translation keys. This is useful for
// maintaining backwards compatibility with older translations while
// transitioning to new keys.

// The hope is to handle this rather at the db level where deploying an updated
// translation will automatically update the translation keys in the db for all
// affected translations.

// One gap with this method currently is that the translation variables are not updated
// in the legacy translations, so the client must still pass in the legacy translation
// variables to the translation function. We'll want to fix this in the future.
// Another gab: we should have the client attempt to generate the deprecated translation
// json when new keys are added to the translation json to further automate this process.

// The deprecated common json translation is a list of maps of flattened deprecated translation paths
// to a new translation path. This fn applies the map to the provided translation.
// Add a new translation map per commit, adding to the end of the array so we apply
// the most recent translations last.
export function applyDeprecatedTranslations(
  translation: TranslationJSON
): TranslationJSON {
  // Now, map any deprecated keys to the new keys.
  deprecatedCommonTranslations.forEach((deprecatedCommonTranslation) => {
    Object.entries(deprecatedCommonTranslation).forEach(
      ([deprecatedPath, newPath]) => {
        // NOTE: this keySeparatorRe is ported over from the feat/home-screen branch,
        // but also is slightly improved to handle preceding/following spaces.
        const keySeparatorRe = /(?<=[^\. ])\.(?=[^\. ])(?!(?:_one|_other)$)/
        let deprecatedKeys = deprecatedPath.split(keySeparatorRe)
        let newKeys = newPath.split(keySeparatorRe)
        let deprecatedTranslation: Record<string, unknown> = translation
        let newTranslation: Record<string, unknown> = translation

        // Loop through the deprecated keys to find if the deprecated translation path exists.
        while (deprecatedKeys.length) {
          const deprecatedKey = deprecatedKeys.shift()
          if (deprecatedKey && deprecatedTranslation[deprecatedKey]) {
            // When no more keys exist, we can try mapping the deprecated translation to the
            // new translation path.
            if (!deprecatedKeys.length) {
              // Loop through the new keys to find if the new translation path exists.
              while (newKeys.length) {
                const newKey = newKeys.shift()
                if (newKey && newTranslation[newKey]) {
                  // When no more keys exist, we can try mapping the deprecated translation to the
                  // new translation path.
                  if (!newKeys.length) {
                    // Finally mapping the deprecated translation value to the new translation path.
                    newTranslation[newKey] =
                      deprecatedTranslation[deprecatedKey]
                  }
                } else {
                  // Break out when we reach a key that doesn't exist in the new translation path.
                  break
                }

                // Update the new translation to the nested object.
                if (newKey) {
                  newTranslation = newTranslation[newKey] as Record<
                    string,
                    unknown
                  >
                }
              }
            }
          } else {
            // Break out when we reach a key that doesn't exist in the deprecated translation path.
            break
          }

          // Update the deprecated translation to the nested object.
          if (deprecatedKey) {
            deprecatedTranslation = deprecatedTranslation[
              deprecatedKey
            ] as Record<string, unknown>
          }
        }
      }
    )
  })

  return translation
}
